import {useCallback, useEffect, useRef, useState} from "react";

type DraftData = {
    tabs: Record<string, string>;
    timestamp: number;
};

type UseLocalDraftResult = {
    saveDraft: (tabs: Record<string, string>) => void;
    loadDraft: () => DraftData | null;
    clearDraft: () => void;
    hasDraftConflict: boolean;
    setHasDraftConflict: (value: boolean) => void;
    draftTimestamp: number | null;
};

function getDraftKey(namespace: string, moduleId: string): string {
    return `module-draft:${namespace}/${moduleId}`;
}

export function useLocalDraft(namespace: string, moduleId: string): UseLocalDraftResult {
    const [hasDraftConflict, setHasDraftConflict] = useState(false);
    const [draftTimestamp, setDraftTimestamp] = useState<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    var key = getDraftKey(namespace, moduleId);

    var saveDraft = useCallback((tabs: Record<string, string>) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            var data: DraftData = {tabs, timestamp: Date.now()};
            localStorage.setItem(key, JSON.stringify(data));
        }, 1000);
    }, [key]);

    var loadDraft = useCallback((): DraftData | null => {
        var raw = localStorage.getItem(key);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as DraftData;
        } catch {
            return null;
        }
    }, [key]);

    var clearDraft = useCallback(() => {
        localStorage.removeItem(key);
        setHasDraftConflict(false);
        setDraftTimestamp(null);
    }, [key]);

    useEffect(() => {
        var draft = loadDraft();
        if (draft) {
            setDraftTimestamp(draft.timestamp);
            setHasDraftConflict(true);
        } else {
            setDraftTimestamp(null);
            setHasDraftConflict(false);
        }
    }, [loadDraft]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return {saveDraft, loadDraft, clearDraft, hasDraftConflict, setHasDraftConflict, draftTimestamp};
}
